import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Active, closestCenter, DndContext, Over } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import clsx from 'clsx'
import { v4 as uuid } from 'uuid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ConditionNumericOperator,
  ConditionTextOperator,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DATE_OPERATOR_WITH_RHS,
  FunctionIfsThenExpression,
  FunctionIfsThenRepeaterProps,
  NUMERIC_OPERATOR_WITH_INPUT_RHS,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  OPERATOR_WITH_BETWEEN_RHS,
  OPERATOR_WITH_INPUT_RHS,
  TableAdapter,
} from '@makyoapp/makyo-common'
import { Plus16, Reorder16, Trash16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant } from '@makyoapp/makyo-ui'

import { useSortableHandle } from '../../hooks/useSortableHandle'
import { getOperatorDefaultOption } from '../../shared/condition'
import { ColumnOrCustomDd } from './ColumnOrCustomDd'
import { FunctionConditionOperatorSelect } from './FunctionConditionOperatorSelect'
import { FunctionConditionSelect } from './FunctionConditionSelect'

interface Props extends FunctionIfsThenRepeaterProps {
  tables: TableAdapter[]
  columnID?: string
}

export const FunctionIfsThenRepeaterContext = createContext<Props>({
  value: {
    conditions: [],
  },
  colOptions: [],
  onChange: () => null,
  table: {
    id: '',
    name: '',
  } as TableAdapter,
  tables: [],
  columnID: undefined,
  rhsCustomFieldOnly: false,
})

export function useFunctionIfsThenRepeaterContext() {
  return useContext(FunctionIfsThenRepeaterContext)
}

export function FunctionIfsThenItem({ expression }: { expression: FunctionIfsThenExpression }) {
  const { id, onChange, value, colOptions, table, tables, columnID, label, rhsCustomFieldOnly } =
    useFunctionIfsThenRepeaterContext()

  const index = useMemo(() => {
    return value.conditions.findIndex((c) => c.id === expression.id)
  }, [expression, value.conditions])

  const cond = value.conditions[index]
  //const isRhsColumn = cond.lhs.type === ConditionType.Column
  const colObj = useMemo(() => {
    return colOptions.find((c) => c.id === cond.lhs)
  }, [colOptions, cond.lhs])

  const varColTypes = useMemo(() => {
    if (colObj) {
      if (
        colObj.type === ColumnTypes.Text &&
        [ConditionTextOperator.IsNotContainedIn, ConditionTextOperator.IsContainedIn].includes(
          cond.operator as ConditionTextOperator
        )
      ) {
        return [ColumnTypes.Text, ColumnTypes.Array]
      } else if ([ColumnTypes.Rating, ColumnTypes.Number].includes(colObj.type)) {
        return [ColumnTypes.Rating, ColumnTypes.Number]
      } else {
        return [colObj.type]
      }
    } else {
      return []
    }
  }, [colObj, cond])

  const { attributes, listeners, setNodeRef, transition, transform } = useSortable({
    id: cond.id ?? uuid(),
  })

  const { t } = useTranslation()

  const isDateSingleOperator = useMemo(() => {
    return DATE_OPERATOR_WITH_RHS.includes(cond.operator)
  }, [cond.operator])

  const isDateRangeOperator = useMemo(() => {
    return DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)
  }, [cond.operator])

  const isDateOperator = isDateSingleOperator || isDateRangeOperator

  return (
    <div className='flex-c min-w-0'>
      <div
        ref={setNodeRef}
        {...attributes}
        style={{
          transform: CSS.Translate.toString(transform),
          transition,
        }}
        className='flex-c p-2 border bdr rounded-md bg-zinc-50 dark:bg-slate-700 min-w-0 w-full'
      >
        <div className='flex flex-col gap-2 min-w-0 w-full'>
          <div className='flex items-center'>
            <div className='text-m-sm shrink-0 w-[36px] self-start pt-2.5'>{label || 'IF'}</div>
            <div className='flex flex-col gap-2 min-w-0 w-full'>
              <div className='flex-c w-full'>
                <div className='w-full min-w-0'>
                  <FunctionConditionSelect
                    toggleClassName='dark:hover:bg-slate-800!'
                    id={`${id}-lhs`}
                    value={cond?.lhs}
                    table={table}
                    onChange={(lhs) => {
                      const nextCond = [...value.conditions]
                      const isSame = nextCond[index].lhs === lhs
                      const newColObj = colOptions.find((c) => c.id === lhs)
                      const currentColObj = colOptions.find((c) => c.id === cond.lhs)

                      // Only reset operator and values if column types are different
                      const shouldReset = newColObj && currentColObj && newColObj.type !== currentColObj.type

                      const nextOp = shouldReset
                        ? getOperatorDefaultOption(newColObj, table, tables)
                        : nextCond[index].operator

                      let extra: Record<string, unknown> = {}

                      if (shouldReset) {
                        if (NUMERIC_OPERATOR_WITH_INPUT_RHS.includes(nextOp as ConditionNumericOperator)) {
                          extra = {
                            rhs: {
                              isCustom: true,
                              value: '0',
                            },
                          }
                        } else if (DATE_OPERATOR_WITH_RHS.includes(nextOp)) {
                          extra = {
                            rhs: {
                              isCustom: true,
                              value: new Date().toISOString(),
                            },
                          }
                        } else if (DATE_OPERATOR_WITH_RANGE_RHS.includes(nextOp)) {
                          extra = {
                            rhs: {
                              isCustom: true,
                              value: new Date().toISOString(),
                            },
                            rhsEnd: {
                              isCustom: true,
                              value: new Date().toISOString(),
                            },
                          }
                        } else if (NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(nextOp as ConditionNumericOperator)) {
                          extra = {
                            rhs: {
                              isCustom: true,
                              value: '0',
                            },
                            rhsEnd: {
                              isCustom: true,
                              value: '0',
                            },
                          }
                        }
                      }

                      nextCond[index] = {
                        ...nextCond[index],
                        lhs: lhs.toString(),
                        operator: nextOp,
                        ...(shouldReset ? extra : {}),
                      }

                      onChange({
                        conditions: nextCond,
                      })
                    }}
                    colOptions={colOptions}
                    excludeColumns={columnID ? [columnID] : undefined}
                  />
                </div>
                <FunctionConditionOperatorSelect
                  id={id ? `${id}-op` : undefined}
                  selected={cond.operator}
                  table={table}
                  value={value.conditions[index]}
                  colOptions={colOptions}
                  onChange={(opt) => {
                    const nextCond = [...value.conditions]
                    let extra = {}
                    if (NUMERIC_OPERATOR_WITH_INPUT_RHS.includes(opt as ConditionNumericOperator)) {
                      extra = {
                        rhs: {
                          isCustom: true,
                          value: '0',
                        },
                      }
                    }
                    if (NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(opt as ConditionNumericOperator)) {
                      extra = {
                        rhs: {
                          isCustom: true,
                          value: '0',
                        },
                        rhsEnd: {
                          isCustom: true,
                          value: '0',
                        },
                      }
                    }
                    if (DATE_OPERATOR_WITH_RANGE_RHS.includes(opt)) {
                      extra = {
                        rhs: {
                          isCustom: true,
                          value: new Date().toISOString(),
                        },
                        rhsEnd: {
                          isCustom: true,
                          value: new Date().toISOString(),
                        },
                      }
                    }
                    if (DATE_OPERATOR_WITH_RHS.includes(opt)) {
                      extra = {
                        rhs: {
                          isCustom: true,
                          value: new Date().toISOString(),
                        },
                      }
                    }
                    nextCond[index] = {
                      ...nextCond[index],
                      operator: opt,
                      ...extra,
                    }
                    onChange({
                      conditions: nextCond,
                    })
                  }}
                />
              </div>
              {OPERATOR_WITH_BETWEEN_RHS.includes(cond.operator) && (
                <div className='uppercase text-m-ss'>
                  <Trans>Lower Bound</Trans>
                </div>
              )}
              {(OPERATOR_WITH_INPUT_RHS.includes(cond.operator) ||
                DATE_OPERATOR_WITH_RHS.includes(cond.operator) ||
                NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(cond.operator as ConditionNumericOperator) ||
                DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)) && (
                <ColumnOrCustomDd
                  inputClassName='dark:hover:bg-slate-800!'
                  inputFocusClassName='dark:bg-slate-800!'
                  withCustomValue
                  withExtraOptions={isDateSingleOperator ? ['date'] : undefined}
                  customTypeOnly={rhsCustomFieldOnly}
                  id={`${id}-rhs`}
                  table={table}
                  customValueType={
                    isDateOperator
                      ? 'date'
                      : NUMERIC_OPERATOR_WITH_INPUT_RHS.includes(cond.operator)
                        ? 'number'
                        : 'text'
                  }
                  value={
                    cond.rhs || {
                      isCustom: true,
                      value: '',
                    }
                  }
                  toggleClassName='dark:hover:bg-slate-800!'
                  // types={varColTypes}
                  onChange={(v) => {
                    const nextCond = [...value.conditions]
                    nextCond[index] = {
                      ...nextCond[index],
                      rhs: v,
                    }
                    onChange({
                      conditions: nextCond,
                    })
                  }}
                  excludeColumns={columnID ? [columnID, cond.lhs] : [cond.lhs]}
                />
              )}
              {OPERATOR_WITH_BETWEEN_RHS.includes(cond.operator) && (
                <div className='uppercase text-m-ss'>
                  <Trans>Upper Bound</Trans>
                </div>
              )}
              {(NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(cond.operator as ConditionNumericOperator) ||
                DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)) && (
                <ColumnOrCustomDd
                  inputClassName='dark:hover:bg-slate-800!'
                  inputFocusClassName='dark:bg-slate-800!'
                  withCustomValue
                  withExtraOptions={isDateSingleOperator ? ['date'] : undefined}
                  id={`${id}-rhs-end`}
                  table={table}
                  customValueType={
                    isDateOperator
                      ? 'date'
                      : NUMERIC_OPERATOR_WITH_INPUT_RHS.includes(cond.operator)
                        ? 'number'
                        : 'text'
                  }
                  customTypeOnly={rhsCustomFieldOnly}
                  value={
                    cond.rhsEnd || {
                      isCustom: true,
                      value: '',
                    }
                  }
                  onChange={(v) => {
                    const nextCond = [...value.conditions]
                    nextCond[index] = {
                      ...nextCond[index],
                      rhsEnd: v,
                    }
                    onChange({
                      conditions: nextCond,
                    })
                  }}
                  excludeColumns={columnID ? [columnID, cond.lhs] : [cond.lhs]}
                />
              )}
            </div>
          </div>
          <div className='flex items-center'>
            <div className='text-m-sm shrink-0 w-[36px]'>THEN</div>
            <div className='flex flex-col gap-2 min-w-0 w-full'>
              <ColumnOrCustomDd
                withCustomValue
                id={`${id}-then`}
                table={table}
                value={cond.then}
                onChange={(v) => {
                  const nextCond = [...value.conditions]
                  nextCond[index] = {
                    ...nextCond[index],
                    then: v,
                  }
                  onChange({
                    conditions: nextCond,
                  })
                }}
                excludeColumns={columnID ? [columnID] : undefined}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='flex flex-col gap-2 items-center'>
        <Button
          variant={ButtonVariant.Ghost}
          icon={<Trash16 />}
          onClick={() => {
            const nextCond = [...value.conditions]
            nextCond.splice(index, 1)
            onChange({
              conditions: nextCond,
            })
          }}
          squared
          noPadding
        />
        <div {...listeners}>
          <Button
            variant={ButtonVariant.Ghost}
            className={clsx('hover:text-primary-500!', 'shrink-0')}
            icon={<Reorder16 />}
            noPadding
            squared
            title={t('Reorder')}
            noTip
          />
        </div>
      </div>
    </div>
  )
}

export function FunctionIfsThenRepeater({
  id,
  value,
  onChange,
  colOptions,
  table,
  tables,
  columnID,
  label,
  rhsCustomFieldOnly,
}: Props) {
  const dragEndCb = useCallback(
    (active: Active, over: Over) => {
      const oldIndex = value?.conditions.findIndex((p) => p.id === active.id)
      const newIndex = value?.conditions.findIndex((p) => p.id === over.id)
      const newValue = arrayMove(value?.conditions, oldIndex, newIndex)
      onChange({
        conditions: newValue,
      })
    },
    [value?.conditions, onChange]
  )

  const { sensors, handleDragEnd } = useSortableHandle(dragEndCb)

  return (
    <FunctionIfsThenRepeaterContext.Provider
      value={{
        id,
        value,
        onChange,
        colOptions,
        table,
        tables,
        columnID,
        label,
        rhsCustomFieldOnly,
      }}
    >
      <div id={id} className='flex flex-col gap-4 w-full'>
        {value && value.conditions.length > 0 && (
          <div className='flex gap-2 flex-col w-full'>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToFirstScrollableAncestor]}
            >
              <SortableContext items={value.conditions}>
                {value.conditions.map((e, i) => {
                  //return <Dummy key={a.id || uuid()} a={a} />
                  return <FunctionIfsThenItem expression={e} key={e.id || uuid()} />
                })}
              </SortableContext>
            </DndContext>
          </div>
        )}

        <button
          className='link flex-c'
          id={id ? `${id}-repeat` : undefined}
          onClick={() => {
            onChange({
              conditions: [
                ...(value?.conditions ?? []),
                {
                  id: uuid(),
                  lhs: colOptions[0]?.id,
                  operator: getOperatorDefaultOption(colOptions[0], table, tables),
                  caseSensitive: true,
                  rhs: {
                    isCustom: true,
                    value: '',
                  },
                  then: {
                    isCustom: true,
                    value: '',
                  },
                },
              ],
            })
          }}
        >
          <Plus16 />
          <span>
            <Trans>Add Criteria</Trans>
          </span>
        </button>
      </div>
    </FunctionIfsThenRepeaterContext.Provider>
  )
}
